import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useLocalization } from "@/lib/localization";
import { formatSessionRole } from "@/lib/sessionLabels";
import { useAuth } from "@/lib/store";

export function SignOutConfirmationDialog({ branchName, onOpenChange, open }) {
  const navigate = useNavigate();
  const { messages } = useLocalization();
  const { logout, session } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const copy = messages.layout;

  async function confirmSignOut(event) {
    event.preventDefault();
    if (signingOut) return;

    setSigningOut(true);
    try {
      const completed = await logout();
      if (!completed) return;
      onOpenChange(false);
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !signingOut && onOpenChange(nextOpen)}>
      <AlertDialogContent
        overlayClassName="!bg-sidebar/[0.55] backdrop-blur-[3px]"
        className="max-w-[380px] gap-0 overflow-hidden rounded-[20px] border border-border/80 bg-card p-0 shadow-[0_30px_80px_-28px_rgba(15,23,42,0.48)]"
      >
        <div className="relative overflow-hidden border-b border-border/60 bg-gradient-to-br from-destructive/[0.08] via-warning/[0.03] to-card px-5 pb-4 pt-5">
          <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-destructive/10 blur-3xl" />
          <AlertDialogHeader className="relative space-y-2 text-left">
            <AlertDialogTitle className="text-xl tracking-[-0.035em]">{copy.signOutTitle}</AlertDialogTitle>
            <AlertDialogDescription className="max-w-sm text-[13px] leading-5">
              {copy.signOutDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/25 p-3">
            <Avatar className="h-9 w-9 shrink-0 ring-1 ring-border">
              <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                {session?.shopName?.slice(0, 2)?.toUpperCase() ?? "SB"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{session?.shopName}</div>
              <div className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{branchName || session?.email || copy.noBranchSelected}</span>
              </div>
            </div>
            <span className="rounded-full border border-border/70 bg-background px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {formatSessionRole(session?.role)}
            </span>
          </div>
        </div>

        <AlertDialogFooter className="border-t border-border/60 bg-muted/15 px-5 py-3.5 sm:justify-between sm:space-x-3">
          <AlertDialogCancel
            disabled={signingOut}
            className="h-9 rounded-xl border-border bg-card px-4 text-foreground hover:bg-muted hover:text-foreground focus-visible:ring-0"
          >
            {copy.cancel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-logout"
            disabled={signingOut}
            onClick={confirmSignOut}
            className="h-9 rounded-xl bg-destructive px-4 text-destructive-foreground shadow-[0_12px_24px_-14px_hsl(var(--destructive))] hover:bg-destructive/90 focus-visible:ring-0"
          >
            {signingOut ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {signingOut ? copy.processing : copy.signOut}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
